import { useEffect, useState } from "react";
import { Copy, Check, X } from "lucide-react";

/** Vite HMR error payload (inlined to avoid moduleResolution issues with vite 8) */
interface ErrorPayload {
  type: "error";
  err: {
    message: string;
    stack: string;
    id?: string;
    frame?: string;
    plugin?: string;
    pluginCode?: string;
    loc?: { file?: string; line: number; column: number };
  };
}

interface TypeScriptError {
  file: string;
  line: number;
  column: number;
  message: string;
  code?: string;
}

export function TypeScriptErrorOverlay() {
  const [errors, setErrors] = useState<TypeScriptError[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    if (import.meta.hot) {
      import.meta.hot.on("vite:error", (payload: ErrorPayload) => {
        const source = payload.err;
        const error: TypeScriptError = {
          file: source.loc?.file || source.id || "Unknown file",
          line: source.loc?.line ?? 0,
          column: source.loc?.column ?? 0,
          message: source.message || "Unknown error",
          code: source.frame,
        };

        setErrors((prev) => [...prev, error]);
        setIsVisible(true);

        console.group("TypeScript Error");
        console.error(`File: ${error.file}:${error.line}:${error.column}`);
        console.error(`Message: ${error.message}`);
        if (error.code) console.error("Code context:", error.code);
        console.groupEnd();
      });
    }
  }, []);

  const copyErrors = () => {
    const errorReport = `## TypeScript Errors

${errors
  .map(
    (error, index) => `
### Error ${index + 1}
- **File**: ${error.file}
- **Location**: Line ${error.line}, Column ${error.column}
- **Message**: ${error.message}

${error.code ? `\`\`\`typescript\n${error.code}\n\`\`\`` : ""}
`,
  )
  .join("\n")}`;

    navigator.clipboard.writeText(errorReport);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isVisible || errors.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-2xl">
      <div className="overflow-hidden rounded-lg bg-red-900 text-white shadow-2xl">
        <div className="flex items-center justify-between bg-red-950 p-4">
          <h3 className="flex items-center gap-2 font-semibold">
            <span className="text-red-400">●</span>
            TypeScript Errors ({errors.length})
          </h3>
          <div className="flex gap-2">
            <button
              onClick={copyErrors}
              className="flex items-center gap-1 rounded bg-red-800 px-3 py-1 text-sm hover:bg-red-700"
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? "Copied!" : "Copy Errors"}
            </button>
            <button
              onClick={() => setIsVisible(false)}
              className="rounded p-1 hover:bg-red-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {errors.map((error, index) => (
            <div key={index} className="border-t border-red-800 p-4">
              <div className="mb-2 font-mono text-sm text-red-200">
                {error.file}:{error.line}:{error.column}
              </div>
              <div className="text-sm">{error.message}</div>
              {error.code && (
                <pre className="mt-2 overflow-x-auto rounded bg-red-950 p-2 text-xs">
                  {error.code}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
